import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  ScrollView,
} from 'react-native';
import { ScreenLayout, Header, FormHeader } from '../components';
import { CatalogueForm, CatalogueFormData } from '../components/catalogues';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { colors, spacing } from '../theme';
import { useThemedStyles } from '../hooks/useThemedStyles';

export const CatalogueCreateScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, showNotification, activeTab, setActiveTab, routeParams } = useAppContext();
  const [loading, setLoading] = useState(false);

  const listId: string | undefined = routeParams?.listId;

  const handleSubmit = async (formData: CatalogueFormData) => {
    try {
      setLoading(true);

      const response = await apiService.createCatalogue({
        name: formData.name,
        description: formData.description,
        currency: formData.currency,
        locale: formData.locale,
        isActive: formData.isActive,
      });

      if (response.statusCode === 1000 && response.data) {
        const catalogueId = response.data._id || response.data.id;
        showNotification('success', 'Success', 'Catalogue created successfully.');
        if (listId && catalogueId) {
          try {
            await apiService.linkDataListRecords(listId, [catalogueId]);
          } catch (err) {
            console.error('[CatalogueCreateScreen] Failed to link new catalogue to data list:', err);
          }
          setTimeout(() => navigateTo('data-list-records', listId), 1500);
        } else {
          setTimeout(() => {
            navigateTo('catalogue-detail', catalogueId);
          }, 1500);
        }
      } else {
        const errorMessage =
          response.errors?.[0]?.message || response.message || 'Failed to create catalogue';
        showNotification('error', 'Error', errorMessage);
      }
    } catch (error: any) {
      console.error('Error creating catalogue:', error);
      const errorMessage = error.message || 
                          error.errors?.[0]?.message || 
                          'Failed to create catalogue';
      showNotification('error', 'Error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (listId) {
      navigateTo('data-list-records', listId);
    } else if (Platform.OS === 'web') {
      window.history.back();
    } else {
      navigateTo('catalogues');
    }
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      formHeader={<FormHeader title="New Catalogue" onBack={handleCancel} />}
    >
      <View style={styles.screen}>
        <ScrollView
          style={styles.container}
          contentContainerStyle={[
            styles.contentContainer,
            Platform.OS === 'web' && styles.contentContainerWeb,
            Platform.OS !== 'web' && styles.contentContainerMobile
          ]}
          showsVerticalScrollIndicator={false}
        >
          <CatalogueForm
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            submitLabel="Create Catalogue"
            loading={loading}
          />
        </ScrollView>
      </View>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  screen: {
    flex: 1,
  },
  container: {
    flex: 1,
    backgroundColor: colors.lightGray,
  },
  contentContainer: {
    padding: spacing.lg,
    paddingBottom: spacing.xl,
  },
  contentContainerMobile: {
    paddingBottom: 100,
  },
  contentContainerWeb: {
    paddingBottom: spacing.sm,
  },
});

